/* eslint-disable no-unused-vars */
/* eslint-disable max-len */

import { PolymerElement } from "@polymer/polymer/polymer-element.js";
import template from "./ns-quantity-selector.html";
import { StringService } from "@nuskin/ns-util";

/**
 * The ns-quantity-selector should be used for
 *
 *
 * <h1>quantity-selector</h1>
 * <demo-snippet>
 *   <template>
 *     <ns-quantity-selector class="demoElement"></ns-quantity-selector>
 *   </template>
 * </demo-snippet>
 *
 */
class NsQuantitySelector extends PolymerElement {
  constructor() {
    super();
    this.dropdownQuantityArr = [];
  }

  /**
   * Transforms dropdownQuantity to an object / Closes dropdown on outside click / Adds event listeners for input error handling
   */
  ready() {
    super.ready();

    this._getCommonStrings();
    this._setDropDownValues();
    this.setEventListeners();
  }

  static get is() {
    return "ns-quantity-selector";
  }

  static get template() {
    let htmlElement = document.createElement("TEMPLATE");
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties() {
    return {
      currentError: {
        type: String,
        value: ""
      },
      /**
       * Number of options in the quantity selection dropdown
       */
      dropdownQuantity: {
        type: Number,
        value: 10
      },
      /**
       * Shows if there was an error
       */
      error: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Determines if the input or dropdown is shown for choosing quantity
       */
      inputType: {
        type: String,
        value: "dropdown",
        reflectToAttribute: true
      },
      /**
       * Max quantity a user can manually enter
       */
      max: {
        type: Number,
        value: 999,
        observer: "_setMax"
      },
      showDropdownScroll: {
        type: Boolean,
        value: true,
        reflectToAttribute: true
      },
      maxNotUsed: {
        type: Boolean,
        value: true,
        reflectToAttribute: true
      },
      classObjectId: {
        type: String,
        value: "default"
      },
      /**
       * Makes clicking on the dropdown trigger the modal opening
       */
      openModal: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Direction for the dropdown to open (determined automatically)
       */
      open: {
        type: String,
        value: "down",
        reflectToAttribute: true
      },
      /**
       * Current quantity selected
       */
      quantity: {
        type: Number,
        value: 1,
        notify: true,
        observer: "_setQuantity"
      },
      /**
       * Shows whether the input field is selected
       */
      state: {
        type: String,
        value: "blur",
        notify: true, // notify is used by ns-product-small as part of a z-index issue
        reflectToAttribute: true
      },
      /**
       * Include 0
       */
      includeZero: {
        type: Boolean,
        value: false
      },
      /**
       * Disables input
       */
      disabled: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Changes the CSS when we are in the UDPD
       */
      isupdp: {
        type: Boolean,
        reflectToAttribute: true
      },
      /**
       * Changes the CSS so UPDP displays correct when there is no variant dropdown
       */
      isupdpnovar: {
        type: Boolean,
        reflectToAttribute: true
      },
      label: {
        type: String
      }
    };
  }

  setEventListeners() {
    // window.addEventListener('click', (e) => {
    //     if (e.target !== this) this.state = "blur";
    // });

    this.$.inputQuantity.addEventListener("keypress", (e) => {
      this.currentError = "";
      if (this.$.inputQuantity.value.length === 3) e.preventDefault();
      // TODO: Switch to using e.key instead of e.keyCode and e.which after we figure out what needs to be handled.
      let key = e.keyCode || e.which; // firefox does not recognize e.keyCode, must use e.which
      if (
        !((key > 95 && key < 106) || (key > 47 && key < 58) || key == 8) ||
        key == 69 ||
        key == 101
      ) {
        e.preventDefault();
      }
      if (key === 13) {
        this.state = "blur";
        this.$.inputQuantity.blur();
      }
    });
    this.$.inputQuantity.addEventListener("input", (e) => {
      this.errorCheck();
      if (this.$.inputQuantity.value <= 0) this.$.inputQuantity.value = "";
    });
    this.$.inputQuantity.addEventListener("blur", (e) => {
      if (!this.$.inputQuantity.value) this.quantity = 1;
    });
  }

  /**
   * Disables input
   */
  disable() {
    this.disabled = true;
  }

  /**
   * Enables input
   */
  enable() {
    this.disabled = false;
  }

  _getCommonStrings() {
    let inheritedStrings = [
      { key: "done", dflt: "Done" },
      { key: "maxQuantityExceeded", dflt: "Low stock. Only %stock% left." },
      // {key: "minQuantity", dflt: "Minimum quantity is 1"},
      { key: "enter", dflt: "Enter" },
      { key: "select", dflt: "Select" },
      { key: "quantity", dflt: "Quantity" }
    ];

    StringService.getStrings(inheritedStrings).then((strings) => {
      this._setStrings(strings);
    });
  }

  _setStrings(strings) {
    this.doneLbl = strings.done;
    this.maxQuantityExceeded = strings.maxQuantityExceeded;
    // this.minQuantity = strings.minQuantity;
    this.enterLbl = strings.enter;
    this.selectLbl = strings.select;
    if (!this.attributes.hasOwnProperty("label")) {
      this.quantityLbl = strings.quantity;
    }
  }

  /**
   * Determines how to handle the back button on the modal
   */
  breadcrumbs() {
    this.inputType === "input"
      ? (this.inputType = "dropdown")
      : this.toggleDropdown();
  }

  /**
   * Close the dropdown
   */
  closeDropdown() {
    this.state = "blur";
  }

  /**
   * Checks for invalid quantity
   */
  errorCheck() {
    if (this.max && this.quantity > this.max)
      this.currentError = this.maxQuantityExceeded.replace("%stock%", this.max);
    // if (this.$.inputQuantity.value <= 0) this.currentError = this.minQuantity;
  }

  /**
   * Open the dropdown
   */
  openDropdown() {
    this.state = "focus";
  }

  /**
   * Sets the active state of the quantity in the dropdown
   */
  setActive(i) {
    this.set("dropdownQuantityArr." + i + ".active", "active");
  }

  /**
   * Sets all active states of the quantities in the dropdown to inactive
   */
  setInactive() {
    for (let i = 0; i < this.dropdownQuantityArr.length; i++) {
      this.set("dropdownQuantityArr." + i + ".active", "inactive");
    }
  }

  /**
   * Handles a quantity being selected by the user
   */
  selectQuantity(e) {
    this.currentError = "";
    this.setInactive();
    this.setActive(e.model.index);
    let i = e.model.index + 1;
    this.quantity = this.includeZero ? i - 1 : i;

    if (
      this.maxNotUsed &&
      (i === this.dropdownQuantityArr.length ||
        (this.includeZero && i === this.dropdownQuantityArr.length - 1))
    ) {
      this.triggerInputField();
      if (!this.openModal) this.toggleDropdown();
    } else {
      this.inputType = "dropdown";
      this.toggleDropdown();
    }
  }

  _setMax() {
    if (this.max < 0) {
      if (this.includeZero) {
        this.max = 0;
      } else {
        this.max = 1;
      }
    }
    this.currentError = "";
    this._setDropDownValues();
    this._setQuantity();
  }

  _setDropDownValues() {
    let dropdownArr = [],
      minMax = Math.min(this.max, this.dropdownQuantity);
    for (let i = 0; i < minMax; i++) {
      dropdownArr.push({
        active: i === 0 ? "active" : "inactive",
        number: i + 1
      });
    }
    if (this.includeZero) {
      dropdownArr[0].active = "inactive";
      dropdownArr.unshift({ active: "active", number: 0 });
    }
    this.maxNotUsed = minMax < this.max;
    this.dropdownQuantityArr = dropdownArr;
    this.showDropdownScroll = minMax > 4;
  }

  /**
   * Checks max input / Sets corresponding dropdown quantity to active if user manually enters a number
   */
  _setQuantity() {
    this.errorCheck();
    this.setInactive();
    if (this.quantity && this.max && this.quantity > this.max)
      this.quantity = this.max;
    if (this.includeZero) {
      this.quantity < this.dropdownQuantityArr.length
        ? this.setActive(this.quantity)
        : this.setActive(this.dropdownQuantityArr.length - 1);
    } else {
      this.quantity - 1 < this.dropdownQuantityArr.length
        ? this.setActive(this.quantity - 1)
        : this.setActive(this.dropdownQuantityArr.length - 1);
    }
  }

  /**
   * Toggle the dropdown and determines which direction the dropdown will open
   */
  toggleDropdown(e) {
    if (this.state === "blur") {
      if (e) this.open = e.clientY > window.innerHeight / 2 ? "up" : "down";
      this.openDropdown();
    } else {
      this.closeDropdown();
    }
  }

  /**
   * Sets manual user input and auto selects the field for easy handling
   */
  triggerInputField() {
    this.inputType = "input";
    let $inputQuantity = this.$.inputQuantity;
    setTimeout(() => {
      $inputQuantity.focus();
      $inputQuantity.select();
    }, 300);
  }
}
customElements.define(NsQuantitySelector.is, NsQuantitySelector);
